import { useMemo } from 'react';
import { get } from 'lodash';
import { useFirebaseCollection } from 'context/firebase';

// Success stories are Academy articles tagged as such (in any language).
const normalize = (text) => String(text).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
export const isSuccessTag = (tag) => {
  const value = normalize(tag);
  return value.includes('caso') || value.includes('success') || value.includes('case stud');
};

// true when the article is a success story in any of its languages
export const isSuccessArticle = (article) => Object.keys(get(article, 'content', {}))
  .filter((key) => key !== 'base')
  .some((key) => get(article, `content.${key}.tags`, []).some(isSuccessTag));

// article language: the current one if it exists, otherwise English, otherwise the first available
const pickLang = (article, language) => {
  const content = get(article, 'content', {});
  if (content[language]) return language;
  if (content.en) return 'en';
  return Object.keys(content).find((key) => key !== 'base') || language;
};

// Returns the success stories (newest first) shaped for cards and menus.
// skip: do not download yet (e.g. the navbar waits until the Clientes menu is opened)
export const useSuccessCases = (language, { skip = false } = {}) => {
  const { data: articles, loading } = useFirebaseCollection('articles', { skip });

  const cases = useMemo(() => {
    if (!articles) return [];
    const all = articles
      .filter(isSuccessArticle)
      .map((article) => {
        const lang = pickLang(article, language);
        const tags = get(article, `content.${lang}.tags`, []);
        return {
          id: article.id,
          img: get(article, 'content.base.img', ''),
          date: get(article, 'content.base.date.seconds', 0),
          title: get(article, `content.${lang}.title`, ''),
          description: get(article, `content.${lang}.description`, ''),
          tag: tags.find((tag) => !isSuccessTag(tag)) || tags[0] || '',
          tags: tags.join(' '),
          inLanguage: Boolean(get(article, `content.${language}`)),
        };
      })
      .sort((a, b) => b.date - a.date);

    // Only cases written in the current language (as in Academia). Some cases are loaded as
    // one article per language, so this also leaves a single case per client.
    const seen = new Set();
    return all
      .filter((item) => item.inLanguage)
      .filter((item) => {
        // eslint-disable-next-line no-use-before-define
        const client = clientForCase(item);
        if (!client) return true;
        if (seen.has(client.name)) return false;
        seen.add(client.name);
        return true;
      });
  }, [articles, language]);

  return { cases, loading: loading || articles === null };
};

export default useSuccessCases;

// Featured clients shown in the navbar "Clientes" menu (same list as the original site).
// `match` words are searched in each success story's title, description and tags.
// `photo` is the client's image from the new site, used on the Clientes page cards.
const CDN = 'https://static.smileweb.net';
export const FEATURED_CLIENTS = [
  { name: 'Banco Provincia', match: ['banco provincia', 'bapro'], photo: `${CDN}/images/banco_provincia.jpg` },
  { name: 'La Segunda', match: ['la segunda'], photo: `${CDN}/public/presentations_images/segunda.jpeg` },
  { name: 'Correos Chile', match: ['correos'], photo: `${CDN}/public/presentations_images/edificio_correos.jpeg` },
  { name: 'ABInBev', match: ['abinbev', 'ab inbev', 'ab-inbev'], photo: `${CDN}/public/presentations_images/abinbev.jpeg` },
  { name: 'AFP Habitat', match: ['habitat'], photo: 'https://s3.sa-east-1.amazonaws.com/static.smileweb.net/public/presentations_images/afp_edificio.jpeg' },
  { name: 'iúnigo', match: ['iunigo'], photo: `${CDN}/public/presentations_images/iunigo_portada.jpeg` },
  { name: 'Asesuisa', match: ['asesuisa'], photo: `${CDN}/public/presentations_images/asesuisa.webp` },
  { name: 'B×+', match: ['bx+', 'bx mas', 'bxmas', 've por mas'], photo: `${CDN}/public/presentations_images/bx%2B.jpeg` },
  { name: 'MAF Perú', match: ['maf'], photo: `${CDN}/public/images/maf.avif` },
  { name: 'Banco Hipotecario El Salvador', match: ['hipotecario el salvador', 'hipotecario de el salvador', 'hipotecario sv'], photo: `${CDN}/public/presentations_images/banco_hipotecario.png` },
  {
    name: 'Banco Hipotecario Argentina', match: ['banco hipotecario'], exclude: ['salvador'], photo: `${CDN}/public/presentations_images/Banco_Hipotecario_S.A..jpeg`,
  },
];

const caseMatchesClient = (item, client) => {
  const text = normalize(`${item.title} ${item.description} ${item.tags}`);
  if ((client.exclude || []).some((word) => text.includes(word))) return false;
  return client.match.some((word) => text.includes(word));
};

// Pairs each featured client with its success story (or null while it has none).
export const matchFeaturedClients = (cases) => FEATURED_CLIENTS.map((client) => {
  const found = cases.find((item) => caseMatchesClient(item, client));
  return { ...client, caseId: found ? found.id : null };
});

// The featured client a success story belongs to (or undefined).
export const clientForCase = (item) => FEATURED_CLIENTS.find((client) => caseMatchesClient(item, client));
